import {
  getZoomUserContext,
  NOT_EMBEDDED,
  subscribeToZoomLifecycle,
  type ZoomEnvironment,
  type ZoomUserContext
} from '@/integrations/zoom';
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

type ZoomContextValue = ZoomEnvironment & {
  /** Increments whenever Zoom resumes the app, so views can resync server state. */
  resumeToken: number;
  /** Meeting-scoped current participant identity. Never persisted to the legacy API. */
  userContext: ZoomUserContext | null;
  refreshUserContext(): Promise<void>;
};

const ZoomContext = createContext<ZoomContextValue>({
  ...NOT_EMBEDDED,
  resumeToken: 0,
  userContext: null,
  refreshUserContext: async () => {}
});

export const useZoom = () => useContext(ZoomContext);

export function ZoomProvider({
  initialEnvironment,
  children
}: {
  initialEnvironment: ZoomEnvironment;
  children: ReactNode;
}) {
  const [environment, setEnvironment] = useState(initialEnvironment);
  const [resumeToken, setResumeToken] = useState(0);
  const [userContext, setUserContext] = useState<ZoomUserContext | null>(null);

  const refreshUserContext = async () => {
    setUserContext(await getZoomUserContext());
  };

  useEffect(() => {
    if (initialEnvironment.embedded) void refreshUserContext();
  }, [initialEnvironment.embedded]);

  useEffect(() => {
    if (!initialEnvironment.embedded) return;

    let unsubscribe: (() => void) | undefined;
    let cancelled = false;

    subscribeToZoomLifecycle({
      onContextChange: (next) => {
        if (!cancelled) {
          setEnvironment(next);
          void refreshUserContext();
        }
      },
      onResume: () => {
        if (!cancelled) setResumeToken((token) => token + 1);
      }
    }).then((teardown) => {
      if (cancelled) {
        teardown();
        return;
      }
      unsubscribe = teardown;
    });

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, [initialEnvironment.embedded]);

  const value = useMemo(
    () => ({ ...environment, resumeToken, userContext, refreshUserContext }),
    [environment, resumeToken, userContext]
  );

  return <ZoomContext.Provider value={value}>{children}</ZoomContext.Provider>;
}
